import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  Star, Users, BookOpen, Loader2, UserPlus, ArrowLeft, Trophy,
  Clock, ChefHat, Heart, UtensilsCrossed, AlertTriangle, Leaf, CalendarDays
} from "lucide-react";
import gustoAvatar from "@/assets/gusto-avatar.png";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { motion } from "framer-motion";
import ChefAvatar from "@/components/ChefAvatar";

const LEVEL_TITLES = [
  "Commis", "Chef de Partie", "Sous-chef", "Chef", "Chef Exécutif",
  "Chef Étoilé", "Meilleur Ouvrier", "Chef Légendaire"
];

const DIFFICULTY_MAP = { easy: "Facile", medium: "Moyen", hard: "Difficile" };

const PublicProfilePage = () => {
  const { userId } = useParams();
  const { user: currentUser, token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingFriend, setAddingFriend] = useState(false);
  const [isFriend, setIsFriend] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/users/profile/${userId}`);
        setProfile(res.data);
        if (token) {
          const friendsRes = await axios.get(`${API}/friends`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setIsFriend(friendsRes.data.some(f => f.id === userId));
        }
      } catch {
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId, token]);

  const handleAddFriend = async () => {
    setAddingFriend(true);
    try {
      await axios.post(`${API}/friends/add/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsFriend(true);
      toast.success("Ami ajouté !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setAddingFriend(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <img src={gustoAvatar} alt="Gusto" className="h-10 opacity-30" />
        <p className="text-muted-foreground">Profil introuvable</p>
        <Link to="/">
          <Button variant="outline" className="rounded-full">Retour</Button>
        </Link>
      </div>
    );
  }

  const levelTitle = LEVEL_TITLES[Math.min((profile.level || 1) - 1, LEVEL_TITLES.length - 1)];
  const isOwnProfile = currentUser?.id === userId;
  const dp = profile.dietary_profile || {};
  const hasDiet = dp.diet_type || dp.allergies?.length || dp.preferences?.length;
  const recipes = profile.recipes || [];
  const favCuisines = profile.favorite_cuisines || [];
  const meals = profile.meals || [];

  return (
    <div className="min-h-screen pb-24 px-4 pt-4" data-testid="public-profile-page">
      <div className="max-w-2xl mx-auto space-y-5">
        <Link to={-1} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-charcoal transition-colors">
          <ArrowLeft className="w-4 h-4" /> Retour
        </Link>

        {/* Profile Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-br from-primary/10 via-accent/10 to-secondary/10 p-6">
              <div className="flex items-center gap-4">
                <ChefAvatar src={profile.avatar} name={profile.pseudo} size="xl" showToque={true} />
                <div className="flex-1">
                  <h1 className="font-heading text-2xl font-bold text-charcoal" data-testid="profile-display-name">
                    Chef(fe) {profile.pseudo}
                  </h1>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Badge className="bg-primary/10 text-primary">
                      <ChefHat className="w-3 h-3 mr-1" />
                      {levelTitle}
                    </Badge>
                    {profile.total_points > 0 && (
                      <Badge className="bg-amber-100 text-amber-700">
                        <Trophy className="w-3 h-3 mr-1" />
                        {profile.total_points} pts
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <CardContent className="p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 rounded-xl bg-blue-50">
                  <BookOpen className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                  <p className="text-xl font-bold text-charcoal">{profile.recipe_count}</p>
                  <p className="text-xs text-muted-foreground">Recettes</p>
                </div>
                <div className="p-3 rounded-xl bg-green-50">
                  <Users className="w-5 h-5 mx-auto text-green-500 mb-1" />
                  <p className="text-xl font-bold text-charcoal">{profile.friends_count}</p>
                  <p className="text-xs text-muted-foreground">Amis</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-50">
                  <Star className="w-5 h-5 mx-auto text-amber-500 mb-1" />
                  <p className="text-xl font-bold text-charcoal">
                    {profile.avg_rating > 0 ? profile.avg_rating : "-"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {profile.rating_count > 0 ? `${profile.rating_count} avis` : "Note"}
                  </p>
                </div>
              </div>

              {!isOwnProfile && token && !isFriend && (
                <Button onClick={handleAddFriend} disabled={addingFriend} className="w-full mt-4 rounded-full gap-2" data-testid="add-friend-btn">
                  {addingFriend ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  Ajouter en ami
                </Button>
              )}
              {isFriend && (
                <div className="mt-4 text-center">
                  <Badge className="bg-green-100 text-green-700 px-4 py-1.5">
                    <Users className="w-3 h-3 mr-1" /> Ami
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Dietary Profile / Tastes */}
        {hasDiet && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card>
              <CardContent className="p-4">
                <h3 className="font-heading font-semibold text-charcoal mb-3 flex items-center gap-2">
                  <Leaf className="w-4 h-4 text-green-500" /> Goûts & Régime alimentaire
                </h3>
                <div className="flex flex-wrap gap-2">
                  {dp.diet_type && dp.diet_type !== "omnivore" && (
                    <Badge className="bg-green-50 text-green-700 border-green-200">{dp.diet_type}</Badge>
                  )}
                  {dp.diet_type === "omnivore" && (
                    <Badge variant="outline">Omnivore</Badge>
                  )}
                  {dp.allergies?.map((a) => (
                    <Badge key={a} variant="outline" className="bg-red-50 text-red-600 border-red-200">
                      <AlertTriangle className="w-3 h-3 mr-1" /> {a}
                    </Badge>
                  ))}
                  {dp.preferences?.map((p) => (
                    <Badge key={p} variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                      <Heart className="w-3 h-3 mr-1" /> {p}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Favorite Cuisines */}
        {favCuisines.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card>
              <CardContent className="p-4">
                <h3 className="font-heading font-semibold text-charcoal mb-3 flex items-center gap-2">
                  <UtensilsCrossed className="w-4 h-4 text-primary" /> Cuisines favorites
                </h3>
                <div className="flex flex-wrap gap-2">
                  {favCuisines.map((c) => (
                    <Badge key={c.name} className="bg-primary/10 text-primary px-3 py-1">
                      {c.name} ({c.count})
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Meals / Repas */}
        {meals.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}>
            <Card>
              <CardContent className="p-4">
                <h3 className="font-heading font-semibold text-charcoal mb-3 flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-red-500" /> Repas ({meals.length})
                </h3>
                <div className="space-y-2">
                  {meals.map((m) => (
                    <Link key={m.id} to={`/meals/${m.id}`}>
                      <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <CalendarDays className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-charcoal truncate">{m.title}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {m.meal_date && (
                              <span>{new Date(m.meal_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>
                            )}
                            {m.recipe_name && <span>· {m.recipe_name}</span>}
                            {m.host_id === userId && <Badge className="text-[9px] bg-primary/10 text-primary px-1.5">Hôte</Badge>}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Recipes Grid */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold text-charcoal flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-blue-500" />
              {recipes.length > 0 ? `Recettes (${profile.recipe_count})` : "Recettes"}
            </h3>
          </div>
          {recipes.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {recipes.map((recipe, i) => (
                <motion.div
                  key={recipe.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 + i * 0.05 }}
                >
                  <Link to={`/recipe/${recipe.id}`} data-testid={`recipe-card-${recipe.id}`}>
                    <Card className="overflow-hidden hover:shadow-md transition-shadow group cursor-pointer h-full">
                      <div className="aspect-[4/3] bg-muted relative overflow-hidden">
                        {(recipe.image_url || recipe.final_image) ? (
                          <img
                            src={recipe.image_url || recipe.final_image}
                            alt={recipe.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
                            <UtensilsCrossed className="w-8 h-8 text-muted-foreground/30" />
                          </div>
                        )}
                        {recipe.difficulty && (
                          <Badge className="absolute top-2 right-2 text-[10px] bg-white/90 text-charcoal">
                            {DIFFICULTY_MAP[recipe.difficulty] || recipe.difficulty}
                          </Badge>
                        )}
                      </div>
                      <CardContent className="p-2.5">
                        <p className="text-sm font-medium text-charcoal truncate">{recipe.name || "Recette sans nom"}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          {recipe.prep_time > 0 && (
                            <span className="flex items-center gap-0.5">
                              <Clock className="w-3 h-3" /> {recipe.prep_time} min
                            </span>
                          )}
                          {recipe.cuisine_type && (
                            <span className="truncate">{recipe.cuisine_type}</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <Card className="text-center py-8">
              <CardContent>
                <UtensilsCrossed className="w-10 h-10 mx-auto text-muted-foreground/20 mb-2" />
                <p className="text-sm text-muted-foreground">
                  {profile.pseudo} n'a pas encore partagé de recettes
                </p>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PublicProfilePage;
